'use client';

import React, { createContext, useContext, useEffect, ReactNode } from 'react';

export type Language = 'ko' | 'en' | 'zh' | 'mn';

export const LANGUAGES: Language[] = ['ko', 'en', 'zh', 'mn'];

// next.config.ts의 basePath와 반드시 동일하게 유지할 것.
// 일반 <a> 태그에는 Next가 basePath를 자동으로 붙여주지 않는다.
export const BASE_PATH = '/universal-test-landing';

// 언어별 경로 접두어 (한국어는 기존 URL 유지를 위해 접두어 없음)
export const LANG_PREFIX: Record<Language, string> = {
    ko: '',
    en: '/en',
    zh: '/zh',
    mn: '/mn'
};

const HTML_LANG: Record<Language, string> = {
    ko: 'ko',
    en: 'en',
    zh: 'zh-CN',
    mn: 'mn'
};

interface LanguageContextType {
    language: Language;
    t: (key: any) => any;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * 언어는 URL 경로로 결정된다 (/ = ko, /en = en, /zh = zh).
 * 서버에서 렌더링한 HTML과 클라이언트 첫 렌더가 항상 같은 언어이므로
 * hydration 불일치가 발생하지 않는다.
 */
export function LanguageProvider({
    language,
    children
}: {
    language: Language;
    children: ReactNode;
}) {
    useEffect(() => {
        document.documentElement.lang = HTML_LANG[language];
    }, [language]);

    const t = (data: any) => {
        if (!data) return '';
        return data[language] || data['ko'] || '';
    };

    return (
        <LanguageContext.Provider value={{ language, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useLanguage must be used within a LanguageProvider');
    }
    return context;
}
